import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import Button from '@/components/ui/Button';
import { HolidayPeriodDtoIn } from '@/models/in/HolidayPeriodDtoIn';
import { HolidayPeriodDtoOut } from '@/models/out/HolidayPeriodDtoOut';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { postAuth, putAuth } from '@/api/ApiCalls';

const QUERY_URL = 'management/holiday-periods';

interface EditOrCreateHolidayPeriodDialogProps {
  open: boolean;
  onClose: () => void;
  periodToEdit?: HolidayPeriodDtoIn | null;
  onSuccess: () => void;
}

const formatDateForInput = (dateStr: string): string => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const EditOrCreateHolidayPeriodDialog = ({
  open,
  onClose,
  periodToEdit,
  onSuccess,
}: EditOrCreateHolidayPeriodDialogProps) => {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();
  const headers = { Authorization: `Bearer ${currentUser?.token}` };

  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditing = !!periodToEdit;

  useEffect(() => {
    if (open) {
      if (periodToEdit) {
        setName(periodToEdit.name);
        setStartDate(formatDateForInput(periodToEdit.startDate));
        setEndDate(formatDateForInput(periodToEdit.endDate));
      } else {
        setName('');
        setStartDate('');
        setEndDate('');
      }
      setError(null);
    }
  }, [open, periodToEdit]);

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError(t('holidayPeriodsManagement.dialog.nameRequired'));
      return;
    }
    if (!startDate || !endDate) {
      setError(t('holidayPeriodsManagement.dialog.invalidDates'));
      return;
    }
    if (new Date(endDate) < new Date(startDate)) {
      setError(t('holidayPeriodsManagement.dialog.invalidDates'));
      return;
    }

    setError(null);
    setIsSubmitting(true);

    const body: HolidayPeriodDtoOut = { name: name.trim(), startDate, endDate };

    try {
      if (isEditing) {
        await putAuth<HolidayPeriodDtoIn, object, HolidayPeriodDtoOut>(
          `${QUERY_URL}/${periodToEdit!.id}`, headers, {}, body
        );
      } else {
        await postAuth<HolidayPeriodDtoIn, HolidayPeriodDtoOut>(QUERY_URL, headers, body);
      }
      onSuccess();
      handleClose();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : '';
      const errorKey = isEditing
        ? 'holidayPeriodsManagement.dialog.errorUpdating'
        : 'holidayPeriodsManagement.dialog.errorCreating';

      if (errorMessage.includes('overlap')) {
        setError(t('holidayPeriodsManagement.dialog.overlapError'));
      } else {
        setError(t(errorKey));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setName('');
    setStartDate('');
    setEndDate('');
    setError(null);
    onClose();
  };

  return (
    <MainDialogLayout
      open={open}
      onOpenChange={(isOpen: boolean) => {
        if (!isOpen) handleClose();
      }}
      title={isEditing
        ? t('holidayPeriodsManagement.dialog.editTitle')
        : t('holidayPeriodsManagement.dialog.createTitle')
      }
    >
      <div className="flex flex-col gap-4 p-4">
        <div className="flex flex-col gap-1">
          <label htmlFor="hp-name" className="body-sm-semibold text-(--font-secondary)">
            {t('holidayPeriodsManagement.name')}
          </label>
          <input
            id="hp-name"
            type="text"
            className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (error) setError(null);
            }}
            placeholder={t('holidayPeriodsManagement.dialog.namePlaceholder')}
          />
        </div>

        <div className="flex gap-4">
          <div className="flex flex-col gap-1 flex-1">
            <label htmlFor="hp-start" className="body-sm-semibold text-(--font-secondary)">
              {t('holidayPeriodsManagement.startDate')}
            </label>
            <input
              id="hp-start"
              type="date"
              className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                if (error) setError(null);
              }}
              dir="ltr"
            />
          </div>
          <div className="flex flex-col gap-1 flex-1">
            <label htmlFor="hp-end" className="body-sm-semibold text-(--font-secondary)">
              {t('holidayPeriodsManagement.endDate')}
            </label>
            <input
              id="hp-end"
              type="date"
              className="border border-(--border-main) rounded-lg px-3 py-2 body-sm-regular"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                if (error) setError(null);
              }}
              dir="ltr"
            />
          </div>
        </div>

        {error && (
          <p className="text-red-500 body-sm-regular">{error}</p>
        )}

        <div className="flex gap-3 justify-end mt-2">
          <Button
            variant="white"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            {t('close')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? t('saving') : t('save')}
          </Button>
        </div>
      </div>
    </MainDialogLayout>
  );
};

export default EditOrCreateHolidayPeriodDialog;
